"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.personScheduleSchema = exports.saveWorkScheduleAssignmentRequestSchema = exports.workScheduleAssignmentSchema = exports.updateWorkScheduleTemplateRequestSchema = exports.createWorkScheduleTemplateRequestSchema = exports.workScheduleTemplateSchema = exports.WORK_SCHEDULE_TYPES = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Work schedules — C43, Phase 3.                                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A workspace curates **templates**; a person is given **assignments**, each from a date. The
 * second half is the whole point: `decisions.md`' C43 row calls a person's schedule *"a timeline,
 * not a field"*, because a plan for February has to stay what it was after somebody changes to four
 * days a week in March.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * ⚠️⚠️ **`flex` HAS NO DAILY VALUES, AND THAT IS THE CARRIED QUESTION RATHER THAN AN OVERSIGHT.**
 * `PHASE-3.md` §6: the mockup's `dayCheck` reads daily hours only for `fix`, so every `flex` day
 * comes out *nincs napi terv*. Whether flex should instead be judged weekly is not answered
 * anywhere in the file, and C44 is where it gets decided. This type says what is stored and takes
 * no position on what it means.
 */
exports.WORK_SCHEDULE_TYPES = ['fix', 'flex'];
/**
 * ⚠️ **Seven values, Monday first, as STRINGS.** `numeric` crosses the driver as a string — the
 * same rule money and logged hours follow — so the client formats and never arithmetics them
 * without parsing first.
 */
const daysSchema = zod_1.z.array(zod_1.z.string()).length(7);
/** Seven daily hours on the way IN, where a form has numbers rather than the driver's strings. */
const dayHoursSchema = zod_1.z.array(zod_1.z.number().min(0).max(24)).length(7);
exports.workScheduleTemplateSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `schedule_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('schedule'),
    name: zod_1.z.string(),
    type: zod_1.z.enum(exports.WORK_SCHEDULE_TYPES),
    days: daysSchema,
    weeklyHours: zod_1.z.string(),
    active: zod_1.z.boolean(),
    sortOrder: zod_1.z.number().int(),
    /** How many people are on it today — what makes deactivating rather than deleting legible. */
    assignedCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createWorkScheduleTemplateRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    type: zod_1.z.enum(exports.WORK_SCHEDULE_TYPES),
    days: dayHoursSchema,
    weeklyHours: zod_1.z.number().min(0).max(168),
    active: zod_1.z.boolean().optional(),
});
exports.updateWorkScheduleTemplateRequestSchema = exports.createWorkScheduleTemplateRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * One assignment.
 *
 * ⚠️⚠️ **THE SHAPE IS RESOLVED ON THE SERVER, WHICH IS WHY THERE IS NO `source` UNION HERE.** A row
 * is stored as either *"this template"* or *"these hours"*, and every reader wants the same four
 * facts out of it: the type, the seven days, the weekly total, and a name to show. Making the
 * client resolve a template id would mean shipping the template list to every screen that draws a
 * person, and giving the two arms different fields would put a branch in each of them.
 */
exports.workScheduleAssignmentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `schedule_assignment_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('scheduleAssignment'),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    fromDate: wireDateSchema,
    /** ⚠️ `null` for a custom assignment — the screen shows *Egyedi*, which is a label not a name. */
    templateId: zod_1.z.string().nullable(),
    templateName: zod_1.z.string().nullable(),
    type: zod_1.z.enum(exports.WORK_SCHEDULE_TYPES),
    days: daysSchema,
    weeklyHours: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️⚠️ **EITHER A TEMPLATE OR A SHAPE, AND THE SCHEMA REFUSES BOTH AND NEITHER.** The database
 * check says the same thing; this one exists so the form is told before it submits, and so the two
 * arms cannot drift into a body that carries a template id AND seven hours nobody will read.
 */
exports.saveWorkScheduleAssignmentRequestSchema = zod_1.z
    .object({
    memberId: zod_1.z.string(),
    fromDate: wireDateSchema,
    templateId: zod_1.z.string().optional(),
    type: zod_1.z.enum(exports.WORK_SCHEDULE_TYPES).optional(),
    days: dayHoursSchema.optional(),
    weeklyHours: zod_1.z.number().min(0).max(168).optional(),
    /** Absent when creating. Present, and checked, when replacing an existing row. */
    version: zod_1.z.number().int().nonnegative().optional(),
})
    .refine((body) => body.templateId === undefined
    ? body.type !== undefined && body.days !== undefined && body.weeklyHours !== undefined
    : body.type === undefined && body.days === undefined && body.weeklyHours === undefined, { message: 'an assignment is either a template or a shape, never both and never neither' });
/**
 * A person's schedule as of one day, already resolved — what the person screen's rail shows.
 *
 * ⚠️⚠️ **`null` MEANS THEY HAVE NONE ON THAT DAY, AND THE ROW SAYS SO RATHER THAN SHOWING ZERO.**
 * Somebody hired in March has no schedule in February, and C44 turns exactly this absence into
 * *nincs napi terv* rather than into a shortfall. The screen has to be able to say the same thing.
 *
 * ⚠️ **`name` is the TEMPLATE's, so a custom assignment has none** — the screen shows *Egyedi*,
 * which is a label rather than a name and therefore belongs in the catalogue.
 */
exports.personScheduleSchema = zod_1.z.object({
    type: zod_1.z.enum(exports.WORK_SCHEDULE_TYPES),
    days: daysSchema,
    weeklyHours: zod_1.z.string(),
    fromDate: wireDateSchema,
    name: zod_1.z.string().nullable(),
});
//# sourceMappingURL=schedules.js.map